// Custom discount system — see /backend/ and docs/custom-discount-system-context.md.
// Every code goes to our Supabase edge function first. If it returns NOT_FOUND
// (or is unreachable), we fall through to Shopify's native /discount endpoint.
const CUSTOM_COUPON_API_BASE = 'https://fjwsowluunpjkevxrfcm.supabase.co/functions/v1';

// Shared state read by the checkout hijack in custom-checkout.js. Do not rename.
window.customCouponState = window.customCouponState || {
  active: false,
  code: null,
  discountCents: 0,
  targetVariantId: null,
  message: '',
};

function hasNativeDiscountApplied() {
  return document.querySelectorAll('.discounts__discount').length > 0;
}

function formatEuros(cents) {
  const locale = document.documentElement.lang || 'de-DE';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

function i18nCoupon(key, fallback) {
  return (window.customCouponI18n && window.customCouponI18n[key]) || fallback;
}

if (!customElements.get('cart-coupon')) {
  customElements.define(
    'cart-coupon',
    class CartCoupon extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="discount_code"]');
        this.applyButton = this.querySelector('.coupon-apply-btn');
        this.errorMessage = this.querySelector('.coupon-error');
        this.successMessage = this.querySelector('.coupon-success');
        this.appliedCoupons = this.querySelector('.applied-coupons');

        if (this.form) {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }

        this.addEventListener('click', this.handleRemoveCoupon.bind(this));

        this.checkExistingDiscounts();
        this.checkCouponApplication();
        this.restoreCustomCouponUI();
        this.bindCartUpdateListener();
      }

      handleSubmit(event) {
        event.preventDefault();

        const discountCode = this.input.value.trim();
        if (!discountCode) {
          this.hideLoading();
          this.showError('Please enter a code.');
          return;
        }

        this.tryCoupon(discountCode);
      }

      async tryCoupon(code) {
        this.showLoading();
        this.clearMessages();

        if (window.customCouponState.active) {
          this.hideLoading();
          this.showError('A discount code is already active. Remove it to apply a different one.');
          return;
        }

        let backendResult = null;
        try {
          const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
          const cart = await cartRes.json();

          const res = await fetch(`${CUSTOM_COUPON_API_BASE}/validate-coupon`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, cart }),
          });
          if (res.ok) backendResult = await res.json();
        } catch (err) {
          console.warn('Custom coupon backend unreachable, falling through to native:', err);
        }

        // Our backend recognized the code AND it passes validation → apply custom flow.
        if (backendResult && backendResult.valid === true) {
          if (hasNativeDiscountApplied()) {
            this.hideLoading();
            this.showError('Please remove the existing discount before applying this code.');
            return;
          }
          window.customCouponState = {
            active: true,
            code: backendResult.code,
            discountCents: backendResult.discount_cents,
            targetVariantId: backendResult.discount_target_variant_id,
            message: backendResult.message,
          };
          this.renderCustomCouponUI(backendResult);
          this.renderDiscountUI(backendResult);
          this.hideLoading();
          this.clearInput();
          this.showSuccess(backendResult.message);
          return;
        }

        // Our backend recognized the code but it's not valid right now
        // (expired, min cart not met, missing qualifying product, etc.) →
        // surface the specific message, don't retry as a native code.
        if (
          backendResult &&
          backendResult.valid === false &&
          backendResult.error !== 'NOT_FOUND'
        ) {
          this.hideLoading();
          this.showError(backendResult.message || 'This code is not valid for your cart.');
          return;
        }

        // Backend said NOT_FOUND, or it was unreachable → try Shopify native.
        this.applyCoupon(code);
      }

      renderCustomCouponUI(data) {
        if (!this.appliedCoupons) return;

        const existing = this.appliedCoupons.querySelector(
          `.coupon-tag[data-coupon-code="${data.code}"]`
        );
        if (!existing) {
          const tag = this.createCouponTag(data.code);
          tag.dataset.couponKind = 'custom';
          this.appliedCoupons.appendChild(tag);
        }
        this.appliedCoupons.style.display = 'block';
      }

      renderDiscountUI(data) {
        // Needs both numbers from the validate-coupon response to render the
        // original-vs-new-total breakdown. Skip silently if we don't have them.
        if (
          !data ||
          typeof data.discount_cents !== 'number' ||
          typeof data.new_cart_total_cents !== 'number'
        ) {
          return;
        }

        const discountCents = data.discount_cents;
        const newCents = data.new_cart_total_cents;
        const originalCents = newCents + discountCents;

        // 1) Insert (or update) the savings breakdown, mirroring the native
        //    .cart-savings block markup so the theme's existing CSS styles it.
        const totalsRow = document.querySelector('.totals')?.closest('.grid.grid-cols-2');
        if (!totalsRow) return;

        let savingsEl = document.querySelector('.cart-savings.custom-coupon-savings');
        if (!savingsEl) {
          savingsEl = document.createElement('div');
          savingsEl.className = 'cart-savings custom-coupon-savings';
          savingsEl.setAttribute('role', 'status');
          savingsEl.setAttribute('aria-live', 'polite');
          savingsEl.innerHTML = `
            <div class="cart-savings__breakdown">
              <div class="cart-savings__row cart-savings__row--original">
                <span class="cart-savings__label text-sm">${i18nCoupon('original_subtotal', 'Subtotal')}</span>
                <span class="cart-savings__value cart-savings__value--struck text-sm" data-original></span>
              </div>
              <div class="cart-savings__row cart-savings__row--discount">
                <span class="cart-savings__label text-sm">${i18nCoupon('you_save', 'You save')}</span>
                <span class="cart-savings__value cart-savings__value--discount text-sm font-medium" data-savings></span>
              </div>
            </div>
          `;
          totalsRow.parentElement.insertBefore(savingsEl, totalsRow);
        }
        savingsEl.querySelector('[data-original]').textContent = formatEuros(originalCents);
        savingsEl.querySelector('[data-savings]').textContent = '-' + formatEuros(discountCents);

        // 2) Update the big subtotal value to the post-discount amount.
        //    Stash the server-rendered original so we can put it back on remove.
        const subtotalEl = document.querySelector('.totals__subtotal-value');
        if (subtotalEl) {
          if (!subtotalEl.dataset.customCouponOrigText) {
            subtotalEl.dataset.customCouponOrigText = subtotalEl.textContent;
          }
          subtotalEl.textContent = formatEuros(newCents);
        }
      }

      clearDiscountUI() {
        const savingsEl = document.querySelector('.cart-savings.custom-coupon-savings');
        if (savingsEl) savingsEl.remove();

        const subtotalEl = document.querySelector('.totals__subtotal-value');
        if (subtotalEl && subtotalEl.dataset.customCouponOrigText) {
          subtotalEl.textContent = subtotalEl.dataset.customCouponOrigText;
          delete subtotalEl.dataset.customCouponOrigText;
        }
      }

      restoreCustomCouponUI() {
        // Cart drawer often re-renders via section fetch; restore our UI from state.
        if (window.customCouponState.active && window.customCouponState.code) {
          this.renderCustomCouponUI({
            code: window.customCouponState.code,
            message: window.customCouponState.message,
          });
          // The stored state doesn't carry the server-fresh subtotal, so kick
          // off a silent re-validation to render the discount breakdown with
          // current numbers.
          this.revalidateCustomCoupon();
        }
      }

      async revalidateCustomCoupon() {
        if (!window.customCouponState.active) return;
        try {
          const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
          const cart = await cartRes.json();
          const res = await fetch(`${CUSTOM_COUPON_API_BASE}/validate-coupon`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: window.customCouponState.code, cart }),
          });
          const data = await res.json();
          if (!data || data.valid !== true) {
            this.clearCustomCouponState();
            this.showError(
              data?.message || 'Your discount is no longer valid for this cart.'
            );
            return;
          }
          window.customCouponState.discountCents = data.discount_cents;
          window.customCouponState.targetVariantId = data.discount_target_variant_id;
          window.customCouponState.message = data.message;
          this.renderDiscountUI(data);
        } catch (err) {
          console.warn('Custom coupon re-validation skipped:', err);
        }
      }

      bindCartUpdateListener() {
        // If the cart changes after a custom coupon is applied, re-validate and
        // refresh the UI. If it's no longer valid (removed qualifying product,
        // fell below min, etc.), clear state so we never ship an invalid
        // discount to /create-draft-order.
        const handler = () => this.revalidateCustomCoupon();
        document.addEventListener('cart:updated', handler);
        document.addEventListener('cart:refresh', handler);
      }

      clearCustomCouponState() {
        window.customCouponState = {
          active: false,
          code: null,
          discountCents: 0,
          targetVariantId: null,
          message: '',
        };
        if (this.appliedCoupons) {
          this.appliedCoupons
            .querySelectorAll('.coupon-tag[data-coupon-kind="custom"]')
            .forEach((el) => el.remove());
        }
        this.clearDiscountUI();
      }

      async applyCoupon(discountCode) {
        try {
          sessionStorage.setItem('applying_coupon', discountCode);

          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && cartDrawer.open) {
            sessionStorage.setItem('cart_drawer_open', 'true');
          }

          const discountUrl = `/discount/${encodeURIComponent(discountCode)}`;
          const response = await fetch(discountUrl, { credentials: 'same-origin' });

          if (response.ok) {
            window.location.reload();
          } else {
            sessionStorage.removeItem('applying_coupon');
            console.error('Discount endpoint returned', response.status);
            this.showError();
            this.hideLoading();
          }
        } catch (error) {
          console.error('Error applying coupon:', error);
          sessionStorage.removeItem('applying_coupon');
          this.showError();
          this.hideLoading();
        }
      }

      handleRemoveCoupon(event) {
        if (event.target.closest('.coupon-remove')) {
          event.preventDefault();
          const tagEl = event.target.closest('.coupon-tag');
          const couponCode = tagEl.dataset.couponCode;
          const isCustom = tagEl.dataset.couponKind === 'custom';
          if (isCustom) {
            this.clearCustomCouponState();
          } else {
            this.removeCoupon(couponCode);
          }
        }
      }

      async removeCoupon(couponCode) {
        try {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && cartDrawer.open) {
            sessionStorage.setItem('cart_drawer_open', 'true');
          }

          const couponTag = this.querySelector(`.coupon-tag[data-coupon-code="${couponCode}"]`);
          if (couponTag) couponTag.remove();

          const cartRes = await fetch('/cart.js');
          const cart = await cartRes.json();
          const items = cart.items.map(item => {
            const entry = { id: item.variant_id, quantity: item.quantity };
            if (item.selling_plan_allocation) {
              entry.selling_plan = item.selling_plan_allocation.selling_plan.id;
            }
            if (item.properties && Object.keys(item.properties).length > 0) {
              entry.properties = item.properties;
            }
            return entry;
          });

          const hostname = window.location.hostname;
          const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
          ['discount_code', 'cart', 'cart_currency', 'cart_sig'].forEach(name => {
            document.cookie = `${name}=; path=/; ${expiry}`;
            document.cookie = `${name}=; path=/; ${expiry}; domain=${hostname}`;
            document.cookie = `${name}=; path=/; ${expiry}; domain=.${hostname}`;
            document.cookie = `${name}=; path=/; ${expiry}; secure`;
            document.cookie = `${name}=; path=/; ${expiry}; domain=${hostname}; secure`;
            document.cookie = `${name}=; path=/; ${expiry}; domain=.${hostname}; secure`;
          });

          if (items.length > 0) {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items })
            });
          }

          if (cart.note || (cart.attributes && Object.keys(cart.attributes).length > 0)) {
            await fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ note: cart.note || '', attributes: cart.attributes || {} })
            });
          }

          window.location.reload();
        } catch (error) {
          console.error('Error removing coupon:', error);
          window.location.reload();
        }
      }

      async refreshCartDrawer() {
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          detail: { open: true }
        }));
      }

      checkExistingDiscounts() {
        const discountElements = document.querySelectorAll('.discounts__discount');
        if (discountElements.length > 0) {
          this.updateAppliedCoupons(discountElements);
        }

        if (this.appliedCoupons) {
          const existingTags = this.appliedCoupons.querySelectorAll('.coupon-tag');
          if (existingTags.length > 0) {
            this.appliedCoupons.style.display = 'block';
          }
        }
      }

      checkCouponApplication() {
        const appliedCoupon = sessionStorage.getItem('applying_coupon');
        const shouldOpenDrawer = sessionStorage.getItem('cart_drawer_open');

        if (appliedCoupon) {
          sessionStorage.removeItem('applying_coupon');

          let couponFound = false;

          const couponTags = this.querySelectorAll('.coupon-tag');
          couponTags.forEach(tag => {
            const code = (tag.dataset.couponCode || '').trim();
            if (code.toLowerCase().includes(appliedCoupon.toLowerCase())) {
              couponFound = true;
            }
          });

          if (!couponFound) {
            const discountElements = document.querySelectorAll('.discounts__discount');
            discountElements.forEach(discount => {
              const discountTitle = discount.textContent.trim().split('-')[0].trim();
              if (discountTitle.toLowerCase().includes(appliedCoupon.toLowerCase())) {
                couponFound = true;
              }
            });
          }

          if (couponFound) {
            this.showSuccess();
          } else {
            this.showError();
          }
        }

        if (shouldOpenDrawer === 'true') {
          sessionStorage.removeItem('cart_drawer_open');

          setTimeout(() => {
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && cartDrawer.show) {
              cartDrawer.show();
            }
          }, 100);
        }
      }

      updateAppliedCoupons(discountElements) {
        if (!this.appliedCoupons) return;

        this.appliedCoupons.innerHTML = '';

        const uniqueCoupons = new Set();

        discountElements.forEach(discount => {
          const discountTitle = discount.textContent.trim().split('-')[0].trim();

          if (!uniqueCoupons.has(discountTitle)) {
            uniqueCoupons.add(discountTitle);
            const couponTag = this.createCouponTag(discountTitle);
            this.appliedCoupons.appendChild(couponTag);
          }
        });
      }

      createCouponTag(couponCode) {
        const tag = document.createElement('div');
        tag.className = 'coupon-tag';
        tag.dataset.couponCode = couponCode;

        tag.innerHTML = `
          <span class="coupon-code">${couponCode}</span>
          <button type="button" class="coupon-remove" aria-label="Remove ${couponCode}">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        `;

        return tag;
      }

      showLoading() {
        if (this.applyButton) {
          this.applyButton.disabled = true;
          if (this.applyButton.hasAttribute('is') && this.applyButton.getAttribute('is') === 'hover-button') {
            this.applyButton.setAttribute('aria-busy', 'true');
          }
          const btnText = this.applyButton.querySelector('.btn-text');
          if (btnText) {
            btnText.textContent = 'Applying...';
          } else {
            this.applyButton.textContent = 'Applying...';
          }
        }
      }

      hideLoading() {
        if (this.applyButton) {
          this.applyButton.disabled = false;
          if (this.applyButton.hasAttribute('is') && this.applyButton.getAttribute('is') === 'hover-button') {
            this.applyButton.removeAttribute('aria-busy');
          }

          const btnText = this.applyButton.querySelector('.btn-text');
          const originalText = this.applyButton.dataset.originalText || 'Apply';
          if (btnText) {
            btnText.textContent = originalText;
          } else {
            this.applyButton.textContent = originalText;
          }
        }
      }

      showError(message) {
        if (this.errorMessage) {
          if (message) this.errorMessage.textContent = message;
          this.errorMessage.style.display = 'block';
          setTimeout(() => this.clearMessages(), 3000);
        }
      }

      showSuccess(message) {
        if (this.successMessage) {
          if (message) this.successMessage.textContent = message;
          this.successMessage.style.display = 'block';
          setTimeout(() => this.clearMessages(), 3000);
        }
      }

      clearMessages() {
        if (this.errorMessage) this.errorMessage.style.display = 'none';
        if (this.successMessage) this.successMessage.style.display = 'none';
      }

      clearInput() {
        if (this.input) {
          this.input.value = '';
        }
      }
    }
  );
}
